import { useEffect, useRef, useState } from "react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import firebaseStorage from "@/firebase/storage";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { CustomBackdropController } from "@/components/backdrop/default/v1";
import { useCreatePostMutation, useUpdatePostMutation } from "@/hooks/ReactQuery/PostReactQuery/PostReactQuery";
import { FgGroupField } from "../../FdShortLink/fragments/FgGroups";
import { hasText } from "../postContent";
import { FgPostTextModule } from "./FgPostTextModule";
import { FgPostImageModule, imageModuleError, toImageModuleBody } from "./FgPostImageModule";
import { setPostPreviewDraft } from "../postPreview";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_TITLE_LENGTH = 100;
const MODULE_BUTTON = "rounded-lg px-2 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:invisible";
// 공개 범위 (서버 PostsUseCase VISIBILITIES 와 같은 값)
const VISIBILITY_OPTIONS = [
    { value: 'PUBLIC', label: '전체 공개', description: '누구나 읽고, 검색에도 걸려요.' },
    { value: 'PASSWORD', label: '🔒 비밀번호', description: '목록엔 제목만 보이고, 비밀번호를 아는 사람만 읽어요. 검색에는 안 걸려요.' },
    { value: 'PRIVATE', label: '나만 보기', description: '페이지에 보이지 않아요. 여기 관리자에서만 읽을 수 있어요.' },
];
const MIN_PASSWORD_LENGTH = 4;
const SEGMENT = (active) => `rounded-lg px-3 py-1.5 text-[13px] font-semibold ring-1 transition ${active ? 'bg-gray-900 text-white ring-gray-900' : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`;
const INSERT_BUTTON = "rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 hover:text-gray-900";

// 편집 중 모듈은 key 를 붙여 둔다 (순서를 바꿔도 Tiptap 편집기가 다시 만들어지지 않게)
let nextKey = 0;
const newTextModule = (blocks = []) => ({ key: ++nextKey, type: 'TEXT', blocks });
const newImageModule = () => ({ key: ++nextKey, type: 'IMAGE', layout: 'SINGLE', urls: [], albumLayout: null, ratio: '1:1' });
const toEditorModule = (m) => (m.type === 'IMAGE' ? { ...newImageModule(), ...m } : newTextModule(m.blocks));
const isEmptyModule = (m) => (m.type === 'IMAGE' ? !m.urls.length : !hasText(m.blocks));

/**
 * 글 쓰기·수정. 본문은 페이지 모듈처럼 텍스트·이미지 모듈을 쌓고 순서를 바꾼다.
 * post 가 없으면 새 글. onSaved(post, { published }) 는 저장 뒤 호출 (새 글이면 주소를 글 ID 로 바꾸는 데 쓴다).
 */
export function FgPostEditor({ userPageId, domainName, post, groups, onBack, onSaved }) {
    const createPostMutation = useCreatePostMutation();
    const updatePostMutation = useUpdatePostMutation();

    const coverInputRef = useRef(null);

    const [title, setTitle] = useState(post?.title || '');
    const [coverImageUrl, setCoverImageUrl] = useState(post?.coverImageUrl || null);
    const [group, setGroup] = useState(post?.group || '');
    const [modules, setModules] = useState(() => (post?.content?.length ? post.content.map(toEditorModule) : [newTextModule()]));
    const [visibility, setVisibility] = useState(post?.visibility || 'PUBLIC');
    const [password, setPassword] = useState(''); // 새로 정하거나 바꿀 때만 (저장된 비밀번호는 서버에만 해시로)
    const [dirty, setDirty] = useState(false);

    const status = post?.status || 'DRAFT';

    // 저장하지 않고 창을 닫으려 하면 묻는다
    useEffect(() => {
        if (!dirty) {
            return;
        }
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    // 오른쪽 글 미리보기 (저장 전 내용 그대로, 모듈 key 로 편집기와 연결)
    useEffect(() => {
        setPostPreviewDraft({ title, coverImageUrl, group, modules, visibility, publishedAt: post?.publishedAt || null });
    }, [title, coverImageUrl, group, modules, visibility, post?.publishedAt]);
    useEffect(() => () => setPostPreviewDraft(null), []);

    const changeModules = (updater) => {
        setModules(updater);
        setDirty(true);
    }
    const updateModule = (key, patch) => changeModules(prev => prev.map(m => (m.key === key ? { ...m, ...patch } : m)));
    const insertModule = (index, module) => changeModules(prev => [...prev.slice(0, index), module, ...prev.slice(index)]);
    const moveModule = (index, to) => changeModules(prev => {
        const next = [...prev];
        next.splice(to, 0, next.splice(index, 1)[0]);
        return next;
    });
    const removeModule = (module) => {
        if (!isEmptyModule(module) && !window.confirm('이 모듈을 뺄까요? 안의 내용도 함께 빠져요.')) {
            return;
        }
        changeModules(prev => prev.filter(m => m.key !== module.key));
    }

    /** 사진 올리기 (5MB 넘는 파일은 건너뜀) → 올린 주소 배열 */
    const uploadImages = async (files) => {
        const images = files.filter(file => file.type.startsWith('image/'));
        const valid = images.filter(file => file.size <= MAX_FILE_SIZE);
        if (valid.length < images.length) {
            customToast.error('5MB가 넘는 사진은 빼고 올렸어요.');
        }
        if (!valid.length) {
            return [];
        }

        CustomBackdropController().showBackdrop();
        try {
            return await Promise.all(valid.map(async (file) => {
                const extension = file.name.split('.').pop();
                const fileRef = ref(firebaseStorage, `/assets/user_pages/${userPageId}/post_images/${uuidv4()}.${extension}`);
                return getDownloadURL((await uploadBytes(fileRef, file)).ref);
            }));
        } catch (error) {
            customToast.error('이미지 업로드에 실패했습니다. 다시 시도해 주세요.');
            return [];
        } finally {
            CustomBackdropController().hideBackdrop();
        }
    }

    const handleSelectCover = async (e) => {
        const [url] = await uploadImages([...(e.target.files || [])].slice(0, 1));
        e.target.value = '';
        if (url) {
            setCoverImageUrl(url);
            setDirty(true);
        }
    }

    const save = (nextStatus) => {
        if (!title.trim()) {
            customToast.error('제목을 입력해 주세요.');
            return;
        }
        const error = modules.map(m => (m.type === 'IMAGE' ? imageModuleError(m) : null)).find(Boolean);
        if (error) {
            customToast.error(error);
            return;
        }
        // 빈 모듈은 빼고 저장
        const content = modules.filter(m => !isEmptyModule(m)).map(m => (m.type === 'IMAGE' ? toImageModuleBody(m) : { type: 'TEXT', blocks: m.blocks }));
        if (nextStatus === 'PUBLISHED' && !content.length) {
            customToast.error('본문을 입력해야 발행할 수 있어요.');
            return;
        }

        if (visibility === 'PASSWORD' && !password && !post?.hasPassword) {
            customToast.error('비밀번호를 정해 주세요.');
            return;
        }
        if (visibility === 'PASSWORD' && password && password.length < MIN_PASSWORD_LENGTH) {
            customToast.error(`비밀번호는 ${MIN_PASSWORD_LENGTH}자 이상으로 정해 주세요.`);
            return;
        }

        const body = { title: title.trim(), coverImageUrl, group, content, status: nextStatus, visibility, ...(visibility === 'PASSWORD' && password ? { password } : {}) };
        const options = {
            onSuccess: (saved) => {
                setDirty(false);
                setPassword('');
                onSaved(saved, { published: nextStatus === 'PUBLISHED' && status !== 'PUBLISHED' });
            },
        };

        if (post?.id) {
            updatePostMutation.mutate({ id: post.id, body }, options);
        } else {
            createPostMutation.mutate({ body: { userPageId, ...body } }, options);
        }
    }

    const handleBack = () => {
        if (dirty && !window.confirm('저장하지 않은 내용이 있어요. 나갈까요?')) {
            return;
        }
        onBack();
    }

    /** 모듈 사이·끝에 넣기 버튼 */
    const insertBar = (index) => (
        <div className="flex items-center justify-center gap-1.5 py-1">
            <button type="button" onClick={() => insertModule(index, newTextModule())} className={INSERT_BUTTON}>＋ 텍스트</button>
            <button type="button" onClick={() => insertModule(index, newImageModule())} className={INSERT_BUTTON}>＋ 이미지</button>
        </div>
    );

    return (
        <section className="flex flex-col rounded-[15px] border border-gray-100 bg-white shadow-[var(--defaultBoxShadow)]">
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 min-[993px]:px-6">
                <button type="button" onClick={handleBack} className="rounded-lg px-2 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-100">← 이야기 목록</button>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${status === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {status === 'PUBLISHED' ? '발행됨' : post?.id ? '임시저장' : '새 이야기'}
                </span>
                {dirty && <span className="text-xs text-gray-400">저장 안 됨</span>}
                {status === 'PUBLISHED' && visibility !== 'PRIVATE' && domainName &&
                    <a href={`https://shool.uk/${domainName}/p/${post.id}`} target="_blank" rel="noreferrer" className="ml-auto text-xs font-semibold text-gray-500 hover:text-gray-900 hover:underline">이야기 보기 ↗</a>
                }
            </div>

            <div className="flex flex-col gap-4 px-4 py-5 min-[993px]:px-6">
                {coverImageUrl
                    ? <div className="group relative overflow-hidden rounded-xl">
                        <img src={coverImageUrl} alt="커버 이미지" className="aspect-[2/1] w-full object-cover" />
                        <div className="absolute right-2 top-2 flex gap-1.5">
                            <button type="button" onClick={() => coverInputRef.current.click()} className="rounded-lg bg-black/60 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-black/75">바꾸기</button>
                            <button type="button" onClick={() => { setCoverImageUrl(null); setDirty(true); }} className="rounded-lg bg-black/60 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-black/75">빼기</button>
                        </div>
                    </div>
                    : <button type="button" onClick={() => coverInputRef.current.click()} className="self-start rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-semibold text-gray-500 hover:border-gray-400 hover:text-gray-700">
                        ＋ 커버 이미지 <span className="font-normal text-gray-400">(선택 · 목록 카드와 공유 미리보기에 보여요. 없으면 본문 첫 사진)</span>
                    </button>
                }

                <input
                    value={title}
                    onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
                    maxLength={MAX_TITLE_LENGTH}
                    placeholder="제목"
                    aria-label="제목"
                    className="w-full border-0 bg-transparent text-[26px] font-extrabold text-gray-900 outline-none placeholder:text-gray-300"
                />

                <div className="min-[600px]:max-w-[320px]">
                    <FgGroupField value={group} onChange={(value) => { setGroup(value); setDirty(true); }} groups={groups} />
                </div>

                <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-gray-600">공개 범위</span>
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="공개 범위">
                        {VISIBILITY_OPTIONS.map(option => (
                            <button key={option.value} type="button" onClick={() => { setVisibility(option.value); setDirty(true); }} aria-pressed={visibility === option.value} className={SEGMENT(visibility === option.value)}>
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-gray-500">{VISIBILITY_OPTIONS.find(o => o.value === visibility).description}</p>
                    {visibility === 'PASSWORD' &&
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setDirty(true); }}
                            maxLength={50}
                            autoComplete="new-password"
                            placeholder={post?.hasPassword ? '비밀번호 정해 둠 · 바꾸려면 새로 입력' : `비밀번호 (${MIN_PASSWORD_LENGTH}자 이상)`}
                            aria-label="이야기 비밀번호"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-900 min-[600px]:max-w-[320px]"
                        />
                    }
                </div>

                <div className="flex flex-col">
                    {modules.map((module, index) => (
                        <div key={module.key}>
                            {index > 0 && insertBar(index)}
                            <div data-post-module={module.key} className="scroll-mt-24 rounded-xl p-3 ring-1 ring-gray-100 transition-shadow focus-within:ring-gray-300 data-[flash]:ring-2 data-[flash]:ring-[var(--mainColor)]">
                                <div className="mb-2 flex items-center gap-1">
                                    <span className="mr-auto text-[11px] font-bold text-gray-400">{module.type === 'IMAGE' ? '이미지' : '텍스트'}</span>
                                    <button type="button" onClick={() => moveModule(index, index - 1)} disabled={index === 0} aria-label="위로" title="위로" className={MODULE_BUTTON}>↑</button>
                                    <button type="button" onClick={() => moveModule(index, index + 1)} disabled={index === modules.length - 1} aria-label="아래로" title="아래로" className={MODULE_BUTTON}>↓</button>
                                    <button type="button" onClick={() => removeModule(module)} className={`${MODULE_BUTTON} hover:text-[var(--redColor1)]`}>빼기</button>
                                </div>
                                {module.type === 'IMAGE'
                                    ? <FgPostImageModule module={module} onChange={(patch) => updateModule(module.key, patch)} uploadImages={uploadImages} />
                                    : <FgPostTextModule
                                        blocks={module.blocks}
                                        onChange={(blocks) => updateModule(module.key, { blocks })}
                                        placeholder={index === 0 ? '오늘의 일기, 소설 한 편, 시, 자기소개... 무엇이든 써 보세요.' : '이어서 쓰기'}
                                    />
                                }
                            </div>
                        </div>
                    ))}
                    <div className="mt-2">{insertBar(modules.length)}</div>
                </div>
            </div>

            <div className="sticky bottom-0 z-20 flex items-center justify-end gap-2 rounded-b-[15px] border-t border-gray-100 bg-white/95 px-4 py-3 backdrop-blur min-[993px]:px-6">
                {status === 'PUBLISHED'
                    ? <>
                        <button type="button" onClick={() => save('DRAFT')} className="rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100">발행 취소</button>
                        <button type="button" onClick={() => save('PUBLISHED')} className="rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-gray-700">저장</button>
                    </>
                    : <>
                        <button type="button" onClick={() => save('DRAFT')} className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">임시저장</button>
                        <button type="button" onClick={() => save('PUBLISHED')} className="rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-gray-700">발행하기</button>
                    </>
                }
            </div>

            <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={handleSelectCover} />
        </section>
    );
}
